import { useEffect, useCallback } from 'react';
import { useDomainConfig } from './useDomainConfig';

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

export const useGoogleAdSense = () => {
  const config = useDomainConfig();

  useEffect(() => {
    const { analytics } = config;
    
    // Only initialize if tracking is enabled and we have an AdSense client ID
    if (!analytics.trackingEnabled || !analytics.adsenseClientId) {
      return;
    }

    // Check if AdSense script is already loaded
    const existingScript = document.querySelector(
      `script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${analytics.adsenseClientId}"]`
    );

    if (existingScript) {
      return; // Already loaded
    }

    // Initialize adsbygoogle array
    window.adsbygoogle = window.adsbygoogle || [];

    // Load Google AdSense script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${analytics.adsenseClientId}`;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);

    // Add CSS for hiding unfilled ads
    const style = document.createElement('style');
    style.textContent = `
      ins.adsbygoogle[data-ad-status="unfilled"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    // Cleanup function
    return () => {
      // Remove the script on cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [config.analytics.adsenseClientId, config.analytics.trackingEnabled]);

  // Function to push ads to the adsbygoogle array
  const pushAd = useCallback(() => {
    if (window.adsbygoogle && config.analytics.trackingEnabled && config.analytics.adsenseClientId) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (error) {
        console.warn('AdSense push error:', error);
      }
    }
  }, [config.analytics.trackingEnabled, config.analytics.adsenseClientId]);

  // Function to refresh ads (useful for SPA navigation)
  const refreshAds = useCallback(() => {
    if (window.adsbygoogle && config.analytics.trackingEnabled) {
      try {
        // Clear existing ads
        const ads = document.querySelectorAll('.adsbygoogle');
        ads.forEach(ad => {
          if (ad.getAttribute('data-adsbygoogle-status') !== 'done') {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
          }
        });
      } catch (error) {
        console.warn('AdSense refresh error:', error);
      }
    }
  }, [config.analytics.trackingEnabled]);

  return {
    pushAd,
    refreshAds,
    isAdSenseEnabled: config.analytics.trackingEnabled && !!config.analytics.adsenseClientId,
    adsenseClientId: config.analytics.adsenseClientId,
    adSlots: config.analytics.adSlots || {}
  };
}; 